import React, { useContext, useEffect } from 'react';

import '@assets/javascripts/lib/trumbowyg.min';

import { AssetTypes } from '@api/v4/assets/assetTypes';
import Tooltip from '@components/common/tooltip/main';
import Label from '@components/library/labels/PrimaryLabel';
import Input from '@components/library/inputs/PrimaryInput';
import { InputType } from '@components/library/inputs/Input.props';
import { Locale } from '@components/common/language_menu/languagesMap';
import TagsInput from './tagsInput';
import { ActionTypes, AssetDetailsDispatch, ValidationErrors } from '../EditTabTypes';
import assetModalContext from '../asset_modal_context';

($ as TrumbowygJqueryStatic).trumbowyg.svgPath = '/icons.svg';

interface DetailsSectionProps {
  assetDescription: string;
  terribleTrumbowygFlag: Locale | '';
}

interface DetailsSectionScopedContext {
  state: {
    editState: DetailsSectionProps;
    errorState: ValidationErrors[];
    initialData: {
      assetType: AssetTypes;
    };
  };
  dispatch: AssetDetailsDispatch;
}

export const initializeTrumbowyg = (assetDescription: string, dispatch: AssetDetailsDispatch): void => {
  ($('#asset-description-textarea') as TrumbowygJquery)
    .trumbowyg({
      semantic: false,
      urlProtocol: true,
      btns: [
        ['viewHTML'],
        ['formatting'],
        ['strong', 'em', 'del'],
        ['link'],
        ['justifyLeft', 'justifyCenter', 'justifyRight', 'justifyFull'],
        ['unorderedList', 'orderedList']
      ]
    })
    .on('tbwchange', (e) => dispatch({ type: ActionTypes.UpdateAsset, payload: { assetDescription: e.target.value } }))
    .on('tbwpaste', (e) => dispatch({ type: ActionTypes.UpdateAsset, payload: { assetDescription: e.target.value } }))
    .trumbowyg('html', assetDescription);
};

const DetailsSection = (): JSX.Element => {
  const { state, dispatch }: DetailsSectionScopedContext = useContext(assetModalContext);
  const { assetDescription } = state.editState;

  const hasDescriptionError = state.errorState.includes(ValidationErrors.MissingDescription);
  // We need to always show description on text assets
  const isTextAssetClassName = state.initialData.assetType === AssetTypes.Text ? 'show-text-asset-description' : '';

  const embedlyLink = 'https://embed.ly/embed/features/providers';
  const tooltipContent = (
    <div>
      {bfTranslate('We automatically fetch titles, descriptions, and embed codes for over 250 providers with ')}
      <a className="embedly-link" href={embedlyLink} rel="noopener noreferrer" target="_blank">Embed.ly</a>
    </div>
  );

  useEffect(() => (
    initializeTrumbowyg(state.editState.assetDescription, dispatch)
  ), [dispatch, state.editState.terribleTrumbowygFlag]); // state.editState.assetDescription intentionally left out of dep array

  useEffect(() => (
    (): void => {
      // kill trumbowyg
      ($('#asset-description-textarea') as TrumbowygJquery).trumbowyg('destroy');
    }
  ), []);

  return (
    <>
      <div className={`description-label-error-container ${isTextAssetClassName}`}>
        <Label
          attributes={{
            htmlFor: 'asset-description-textarea',
          }}
        >
          {bfTranslate('Description')}
        </Label>
        {state.initialData.assetType === AssetTypes.Text
          && (
            <p className={`input-error ${hasDescriptionError ? 'active' : ''}`}>
              {bfTranslate('Asset description required')}
            </p>
          )}
        {state.initialData.assetType === AssetTypes.ExternalMedium
          && (
            <Tooltip
              clickable
              delayHide={500}
              placement="bottom"
              tooltipContent={tooltipContent}
              tooltipId="embedly-pro-tip-tooltip"
              type="light"
            >
              <div className="embedly-pro-tip-tooltip">
                <span
                  aria-hidden
                  aria-label="Embedly Pro Tip"
                  className="icon bff-tip"
                />
                <h4 className="pro-tip-text">{bfTranslate('Pro tip')}</h4>
              </div>
            </Tooltip>
          )}
      </div>
      <div className={`asset-description-container ${hasDescriptionError ? 'invalid' : ''} ${isTextAssetClassName}`}>
        <Input
          attributes={{
            id: 'asset-description-textarea',
            type: InputType.Textarea,
            name: 'description',
            value: assetDescription,
            onChange: (e: InputChangeEvent): void => dispatch(
              { type: ActionTypes.UpdateAsset, payload: { assetDescription: e.target.value } }
            )
          }}
          input={{ value: assetDescription }}
        />
      </div>
      <TagsInput />
    </>
  );
};

export default DetailsSection;
